import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import apiRequest from '../../../api/request';
import { formatCurrency } from '../utils/deptDisplay';
import { downloadCSV, makeCSV } from '../../../components/CustomTableWithPagination/csvExport';

const EXPORT_HEADERS = [
  { field: 'transactionDate', headerName: 'Date' },
  { field: 'collectorName', headerName: 'Collector' },
  { field: 'viaFallback', headerName: 'Via Enroller', valueGetter: ({ row }) => (row.viaFallback ? row.enrollerName || 'Yes' : '') },
  { field: 'studentName', headerName: 'Student' },
  { field: 'courseName', headerName: 'Course' },
  { field: 'feeType', headerName: 'Fee Type' },
  { field: 'resolvedDeptName', headerName: 'Attributed Dept' },
  { field: 'amount', headerName: 'Amount' },
  { field: 'remarks', headerName: 'Remarks' },
];

const headCellSx = {
  fontWeight: 700,
  fontSize: '0.78rem',
  color: '#475569',
  background: '#F1F5F9',
  borderBottom: '2px solid #E2E8F0',
  whiteSpace: 'nowrap',
};

const bodyCellSx = {
  py: 1.4,
  px: 2,
  fontSize: '0.85rem',
  borderBottom: '1px solid #F1F5F9',
};

// when the table must fit the dialog, long names wrap onto more lines instead of being cut or pushing the table wider
const wrapSx = { display: 'block', whiteSpace: 'normal', wordBreak: 'break-word' };

const ellipsisSx = {
  maxWidth: 220,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// Money gets its own accent (emerald), kept distinct from the indigo used for the department
// badge in the header - blue answers "which department", green answers "how much."
const MONEY_RGB = '5, 150, 105';
const amountCellSx = (intensity, isTop) => ({
  ...bodyCellSx,
  textAlign: 'right',
  fontWeight: isTop ? 800 : 700,
  whiteSpace: 'nowrap',
  color: isTop ? '#047857' : 'inherit',
  background: intensity > 0 ? `rgba(${MONEY_RGB}, ${(0.05 + intensity * 0.22).toFixed(2)})` : undefined,
});

// A dialog shrunk with `zoom` keeps MUI's percentage limits (width / max-height: 100% minus the 64px margin, which are shares of
// the SCREEN) unscaled, so it would be allowed only `scale` of the room it should get. Dividing them by the scale gives the
// scaled dialog the same share of the screen as the unscaled one (the 1200px max-width still caps it, scaled with the rest).
const scaledPaperSx = (scale) => ({
  zoom: scale,
  width: `calc((100% - 64px) / ${scale})`,
  maxHeight: `calc((100% - 64px) / ${scale})`,
});

// The individual income rows behind one table cell - opened by clicking a department cell,
// the Unidentified cell, or the Total Revenue cell. Fetches fresh every time it opens (no
// caching - this is a drill-down into a specific day, not something worth keeping in memory).
// `popupScale` (optional, 0 < x < 1): shrink the whole dialog to this fraction, for a page that is shown scaled down to fit the
// screen - the dialog then keeps the proportion of the table it was opened from. Unset (or 1) changes nothing.
// `endpoint` (optional): where the payments come from. The Accounts Manager page uses the default; the Project Tracker passes its own
// viewer-scoped one so a project user can only open payments of the department they may see.
// `fitWidth` (optional): the payments table always fits the dialog - columns share the width and long names wrap instead of scrolling
// sideways. Unset = as before.
export default function IncomeDetailDialog({ open, onClose, date, deptId, deptName, month, skillxOnly, popupScale, endpoint = '/department-income-tracker/income-detail', fitWidth = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  // Biggest first by default - this is a "what's driving this number" drill-down, so the
  // transaction most worth looking at should be the first thing you see, not buried in
  // whatever order the DB happened to return rows. Still fully sortable both ways from there.
  const [amountSortDir, setAmountSortDir] = useState('desc');

  useEffect(() => {
    if (!open || !date) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setAmountSortDir('desc');

    const params = { date, month };
    if (deptId !== null && deptId !== undefined) params.deptId = deptId;
    if (skillxOnly) params.skillx = 'true';
    const urlParams = new URLSearchParams(params);

    apiRequest({ method: 'get', url: `${endpoint}?${urlParams}` })
      .then((res) => {
        if (cancelled) return;
        if (res?.status === 'failed') {
          setError(res?.message || 'Failed to load transactions');
        } else {
          setData(res?.data || null);
        }
      })
      .catch(() => { if (!cancelled) setError('Failed to load transactions'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [open, date, deptId, month, skillxOnly, endpoint]);

  const handleExport = () => {
    if (!data?.transactions?.length) return;
    const csv = makeCSV({ headers: EXPORT_HEADERS, rows: data.transactions });
    const safeName = (deptName || 'income').replace(/[^a-z0-9]+/gi, '_');
    downloadCSV(csv, `${safeName}_${date}.csv`);
  };

  const sortedTransactions = useMemo(() => {
    if (!data?.transactions) return [];
    const dir = amountSortDir === 'asc' ? 1 : -1;
    return [...data.transactions].sort((a, b) => dir * (a.amount - b.amount));
  }, [data, amountSortDir]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, minHeight: 420, ...(popupScale && popupScale < 1 ? scaledPaperSx(popupScale) : {}) } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2.25, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#EEF2FF',
              color: '#4F46E5',
              flexShrink: 0,
            }}
          >
            <ReceiptLongRoundedIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {deptName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {date}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={data?.transactions?.length ? 'Export to CSV' : 'Nothing to export'}>
            <span>
              <IconButton onClick={handleExport} disabled={!data?.transactions?.length}>
                <FileDownloadRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={onClose}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={30} />
            <Typography variant="body2" color="text.secondary">Loading transactions…</Typography>
          </Box>
        )}
        {!loading && error && (
          <Typography color="error" sx={{ p: 4, textAlign: 'center' }}>
            {error}
          </Typography>
        )}
        {!loading && !error && data && data.count === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 8, color: 'text.secondary' }}>
            <InboxRoundedIcon sx={{ fontSize: 36, opacity: 0.4 }} />
            <Typography variant="body2">No income transactions for this cell.</Typography>
          </Box>
        )}
        {!loading && !error && data && data.count > 0 && (
          <>
            <Box
              sx={{
                px: 3,
                py: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: `linear-gradient(135deg, rgba(${MONEY_RGB}, 0.08) 0%, rgba(${MONEY_RGB}, 0.02) 100%)`,
                borderBottom: '1px solid #E2E8F0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpRoundedIcon sx={{ color: '#047857', fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {data.count} transaction{data.count > 1 ? 's' : ''}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ color: '#047857', fontWeight: 700, letterSpacing: 0.4, display: 'block' }}>
                  TOTAL REVENUE
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: '#047857', lineHeight: 1.1 }}>
                  {formatCurrency(data.total)}
                </Typography>
              </Box>
            </Box>
            <TableContainer sx={{ maxHeight: 560, ...(fitWidth ? { overflowX: 'hidden' } : {}) }}>
              <Table stickyHeader sx={fitWidth ? { tableLayout: 'fixed', width: '100%', '& .MuiTableCell-root': { px: 1, whiteSpace: 'normal', wordBreak: 'break-word' } } : undefined}>
                {fitWidth && (
                  <colgroup>
                    {[10, 15, 17, 19, 11, 13, 15].map((w, i) => (
                      <col key={i} style={{ width: `${w}%` }} />
                    ))}
                  </colgroup>
                )}
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Date</TableCell>
                    <TableCell sx={headCellSx}>Collector</TableCell>
                    <TableCell sx={headCellSx}>Student</TableCell>
                    <TableCell sx={headCellSx}>Course</TableCell>
                    <TableCell sx={headCellSx}>Fee Type</TableCell>
                    <TableCell sx={headCellSx}>Attributed Dept</TableCell>
                    <TableCell sx={{ ...headCellSx, textAlign: 'right' }}>
                      <TableSortLabel
                        active
                        direction={amountSortDir}
                        onClick={() => setAmountSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
                      >
                        Amount
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const maxAmount = Math.max(0, ...sortedTransactions.map((t) => t.amount));
                    return sortedTransactions.map((t, idx) => {
                      const intensity = maxAmount > 0 ? t.amount / maxAmount : 0;
                      const isTop = maxAmount > 0 && t.amount === maxAmount;
                      return (
                    <TableRow
                      key={t.incomeId}
                      hover
                      sx={{ background: idx % 2 === 1 ? '#FAFBFC' : '#fff' }}
                    >
                      <TableCell sx={{ ...bodyCellSx, ...(fitWidth ? { px: 1.25 } : {}), whiteSpace: 'nowrap', color: 'text.secondary' }}>
                        {t.transactionDate}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <span>{t.collectorName || '—'}</span>
                          {t.viaFallback && (
                            <Tooltip
                              title={
                                t.enrollerName && t.enrollerName !== t.collectorName
                                  ? `Collector's own department isn't tracked - attributed via the course enrollment owner, ${t.enrollerName}`
                                  : "Collector's department isn't tracked"
                              }
                            >
                              <Chip size="small" label="via enroller" variant="outlined" color="warning" sx={{ height: 20, fontSize: '0.65rem' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Tooltip title={t.studentName || ''} disableHoverListener={!t.studentName || t.studentName.length < 22}>
                          <Box component="span" sx={fitWidth ? wrapSx : ellipsisSx}>{t.studentName || '—'}</Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Tooltip title={t.courseName || ''} disableHoverListener={!t.courseName || t.courseName.length < 22}>
                          <Box component="span" sx={fitWidth ? wrapSx : ellipsisSx}>{t.courseName || '—'}</Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Chip size="small" label={t.feeType} variant="outlined" sx={{ height: 22, fontSize: '0.7rem', borderColor: '#E2E8F0' }} />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        {t.resolvedDeptName || <em style={{ opacity: 0.55 }}>none</em>}
                      </TableCell>
                      <TableCell sx={amountCellSx(intensity, isTop)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          {isTop && (
                            <Tooltip title="Largest transaction in this list">
                              <StarRoundedIcon sx={{ fontSize: 16, color: '#F59E0B' }} />
                            </Tooltip>
                          )}
                          {formatCurrency(t.amount)}
                        </Box>
                      </TableCell>
                    </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
